import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1100, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, width: '100%' } as React.CSSProperties,
  chip: { display: 'inline-block', margin: '0 6px 6px 0', padding: '2px 10px', borderRadius: 12, fontSize: 12, background: '#e8f0fe' } as React.CSSProperties,
};

// ═══ Quiz 体验 ═══
function PlayView() {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<{ role: string; text: string }[]>([]);
  const [recs, setRecs] = useState<any[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  const syncCatalog = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/catalog/sync'); alert(`已同步 ${r.data.products_synced} 个商品`); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const start = async () => {
    try {
      const r: any = await post('/api/v1/quiz/start', {});
      setSessionId(r.data.session_id);
      setMsgs([{ role: 'agent', text: r.data.message }]);
      setRecs([]);
    } catch (e: any) { alert(e.message); }
  };

  const send = async () => {
    if (!sessionId || !input) return;
    const text = input;
    setInput('');
    setMsgs(m => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const r: any = await post(`/api/v1/quiz/${sessionId}/answer`, { text });
      setMsgs(m => [...m, { role: 'agent', text: r.data.message }]);
      if (r.data.action === 'recommend') {
        setRecs(r.data.recommendations || []);
        setSessionId(null);
      }
    } catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <>
      <div style={{ ...S.card, display: 'flex', gap: 10 }}>
        <button style={S.btn} disabled={busy} onClick={syncCatalog}>{t('📦 同步产品目录')}</button>
        {!sessionId && <button style={S.btnPrimary} onClick={start}>{t('🎯 开始 Quiz 体验')}</button>}
      </div>
      {(msgs.length > 0) && (
        <div style={S.card}>
          <div style={{ minHeight: 160, maxHeight: 320, overflow: 'auto', marginBottom: 10, padding: 10, background: '#fafafa', borderRadius: 8 }}>
            {msgs.map((m, i) => (
              <div key={i} style={{ marginBottom: 6, textAlign: m.role === 'user' ? 'right' : 'left', fontSize: 13 }}>
                <span style={{ display: 'inline-block', padding: '6px 12px', borderRadius: 12, maxWidth: '75%', background: m.role === 'user' ? '#e6f4ea' : '#e8f0fe' }}>
                  {m.text}
                </span>
              </div>
            ))}
            {busy && <div style={{ fontSize: 12, color: '#888' }}>{t('AI 思考中...')}</div>}
          </div>
          {sessionId && (
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={S.input} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder={t('回答问题...')} />
              <button style={S.btnPrimary} disabled={busy} onClick={send}>{t('发送')}</button>
            </div>
          )}
        </div>
      )}
      {recs.length > 0 && (
        <div style={S.card}>
          <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('🎁 个性化推荐')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {recs.map((r: any) => (
              <div key={r.product_id} style={{ border: '1px solid #eee', borderRadius: 10, padding: 12 }}>
                {r.image_url && <img src={r.image_url} alt={r.title} style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 6, marginBottom: 8 }} />}
                <div style={{ fontWeight: 600, fontSize: 13 }}>{r.title}</div>
                <div style={{ color: '#1a73e8', fontWeight: 700, margin: '4px 0' }}>${r.price}</div>
                <div style={{ fontSize: 12, color: '#666' }}>{r.reason}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ═══ 会话记录 ═══
function SessionsView() {
  const [rows, setRows] = useState<any[]>([]);
  const load = () => get('/api/v1/quiz/sessions').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const push = async (id: string) => {
    try { const r: any = await post(`/api/v1/quiz/sessions/${id}/push-klaviyo`); alert(`Klaviyo 推送状态: ${r.data.status}`); load(); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <div style={S.card}>
      <table style={S.table}>
        <thead><tr><th style={S.th}>{t('客户')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('偏好')}</th><th style={S.th}>{t('推荐数')}</th><th style={S.th}>{t('时间')}</th><th style={S.th}>Klaviyo</th></tr></thead>
        <tbody>
          {rows.map((r: any) => {
            const prefs = JSON.parse(r.preferences_json || '{}');
            const recs = JSON.parse(r.recommendations_json || '[]');
            return (
              <tr key={r.id}>
                <td style={S.td}>{r.customer_email || t('匿名')}</td>
                <td style={S.td}>{r.status}</td>
                <td style={S.td}>{Object.entries(prefs).slice(0, 4).map(([k, v]: any) => <span key={k} style={S.chip}>{k}:{v}</span>)}</td>
                <td style={S.td}>{recs.length}</td>
                <td style={S.td}>{(r.started_at || '').slice(0, 16)}</td>
                <td style={S.td}>
                  {r.status === 'completed' && !r.klaviyo_pushed && <button style={S.btn} onClick={() => push(r.id)}>{t('推送')}</button>}
                  {!!r.klaviyo_pushed && '✅'}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && <tr><td style={S.td} colSpan={6}>{t('暂无会话')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function App() {
  const [tab, setTab] = useState('play');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>ShopQuizAI</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('AI 对话式导购测验 — 多轮诊断 · 个性化推荐 · Klaviyo 触达')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'play')} onClick={() => setTab('play')}>{t('Quiz 体验')}</div>
        <div style={S.tab(tab === 'sessions')} onClick={() => setTab('sessions')}>{t('会话记录')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'play' && <PlayView />}
      {tab === 'sessions' && <SessionsView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
